import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from './store';

// Pages
import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import Expenses from '../pages/Expenses';
import Sessions from '../pages/Sessions';
import Reports from '../pages/Reports';
import Learn from '../pages/Learn';
import Profile from '../pages/Profile';
import Onboarding from '../pages/Onboarding';
import Policy from '../pages/Policy';
import PaywallDebug from '../pages/PaywallDebug';
import DebugWhoami from '../pages/DebugWhoami';
import Metrics from '../pages/Admin/Metrics';
import AbuseQueue from '../pages/Admin/AbuseQueue';
import AdminDashboard from '../pages/Admin/index';
import UserDetail from '../pages/Admin/UserDetail';
import UserManagement from '../pages/Admin/UserManagement';

// Components
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EntitlementGuard from '../components/EntitlementGuard';
import AdminGuard from './adminGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, initialized } = useSelector((state: RootState) => state.auth);

  if (!initialized) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, initialized, user } = useSelector((state: RootState) => state.auth);

  if (!initialized) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    const needsOnboarding = !user?.province || !user?.distanceUnit;
    return <Navigate to={needsOnboarding ? '/onboarding' : '/dashboard'} replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Onboarding route */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route
          path="dashboard"
          element={
            <EntitlementGuard required="PRO">
              <Dashboard />
            </EntitlementGuard>
          }
        />
        <Route
          path="expenses"
          element={
            <EntitlementGuard required="PRO">
              <Expenses />
            </EntitlementGuard>
          }
        />
        <Route
          path="sessions"
          element={
            <EntitlementGuard required="PRO">
              <Sessions />
            </EntitlementGuard>
          }
        />
        <Route
          path="reports"
          element={
            <EntitlementGuard required="ARCHIVE">
              <Reports />
            </EntitlementGuard>
          }
        />
        {/* Learn route remains app-internal; public marketing pages are served statically under /learn/* */}
        <Route path="learn" element={<Learn />} />
        <Route path="profile" element={<Profile />} />
        <Route
          path="admin"
          element={
            <AdminGuard>
              <AdminDashboard />
            </AdminGuard>
          }
        />
        <Route
          path="admin/metrics"
          element={
            <AdminGuard>
              <Metrics />
            </AdminGuard>
          }
        />
        <Route
          path="admin/abuse"
          element={
            <AdminGuard>
              <AbuseQueue />
            </AdminGuard>
          }
        />
        <Route
          path="admin/users"
          element={
            <AdminGuard>
              <UserManagement />
            </AdminGuard>
          }
        />
        <Route
          path="admin/users/:uid"
          element={
            <AdminGuard>
              <UserDetail />
            </AdminGuard>
          }
        />
        <Route path="policy/:type" element={<Policy />} />
        <Route path="paywall" element={<PaywallDebug />} />
        <Route path="debug/whoami" element={<DebugWhoami />} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AppRoutes;
